// ⏱️ BENCHMARK: Tempo total de inicialização
const __BOOT_START = performance.now();
(window as any).__BOOT_START = __BOOT_START;
const __bench = (label: string) => console.log(`⏱️ [BENCH] ${label}: ${(performance.now() - __BOOT_START).toFixed(0)}ms`);
__bench('Script start');

// 🔴 CRÍTICO: Importar ANTES de TUDO para capturar hash inicial!
// Este import executa código no nível do módulo que salva window.location.hash
// ANTES do React Router ou qualquer outro processamento limpar o hash
import './lib/initialHash';

__bench('After initialHash');

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App.tsx';
import './index.css';
import { initLayoutUploadBridge } from './lib/upload/layoutUploadBridge';
import { NotificationProvider, ConfirmationProvider } from './components/feedback';
import { KeyboardProvider } from './contexts/KeyboardContext';
import { ConnectivityProvider } from './contexts/ConnectivityContext';
import { PermissionProvider } from './contexts/PermissionContext';
import { AuthProvider } from './contexts/AuthContext';
import { CorelProvider } from './contexts/CorelContext';
import { OrderProvider } from './contexts/OrderContext';
import { ProdutoCategoriasProvider } from './contexts/ProdutoCategoriasContext';
import { HealthStatusProvider } from './hooks/useHealthStatus';
import { corelCommunication } from './lib/corel/communication';
import { startHealthCheck } from './lib/corel/healthCheck';
import { sendToTerminal } from './lib/terminalLogger';
import { LogCategory } from './config/logging';
import { TokenValidator } from './lib/corel/tokenValidator';
__bench('After all imports (contexts, corel, providers)');

import { AppRouter } from './router/AppRouter';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// ⚠️ CRÍTICO: Expor sendToTerminal no window para que hmac.ts e outros módulos possam acessá-lo
// Isso permite logging de debug HMAC antes da inicialização completa do React
if (typeof window !== 'undefined') {
  (window as any).sendToTerminal = sendToTerminal;

  // ✅ NOVO: Função de diagnóstico global para debug no console do navegador
  // Uso: window.diagnoseWebView() no console do navegador
  (window as any).diagnoseWebView = () => {
    // Usar TokenValidator para diagnóstico completo de tokens
    const tokenDiagnostics = TokenValidator.getDiagnostics();

    const diagnosis = {
      timestamp: new Date().toISOString(),
      environment: {
        protocol: window.location.protocol,
        hostname: window.location.hostname,
        port: window.location.port || 'default',
        origin: window.location.origin,
        isHttps: window.location.protocol === 'https:',
        isProduction: window.location.hostname !== 'localhost' && !window.location.hostname.startsWith('192.168.'),
        userAgent: navigator.userAgent
      },
      webview: {
        hasChrome: !!(window as any)?.chrome,
        hasWebview: !!(window as any)?.chrome?.webview,
        hasPostMessage: typeof (window as any)?.chrome?.webview?.postMessage === 'function'
      },
      // ✅ SIMPLIFICADO: Usar TokenValidator centralizado
      session: {
        ...tokenDiagnostics,
        hasDockerId: !!(window as any)?.dockerId,
        dockerId: (window as any)?.dockerId || 'not-provided'
      },
      communication: {
        bootstrapInitialized: corelCommunication.isBootstrapInitialized(),
        pendingRequests: corelCommunication.getPendingRequestsCount(),
        webviewAvailable: corelCommunication.isWebViewAvailable()
      }
    };

    console.log('📊 [DIAGNÓSTICO WEBVIEW2]');
    console.log('='.repeat(60));
    console.table(diagnosis.environment);
    console.log('WebView:', diagnosis.webview);
    console.log('Session:', diagnosis.session);
    console.log('Communication:', diagnosis.communication);
    console.log('='.repeat(60));

    // Também enviar para o terminal de logs
    sendToTerminal('📊 [DIAGNÓSTICO] Diagnóstico WebView2 executado', diagnosis, LogCategory.CONNECTION);

    return diagnosis;
  };

  // ✅ NOVO: Função para testar envio de mensagem
  // Uso: window.testWebViewMessage() no console do navegador
  (window as any).testWebViewMessage = () => {
    const hasWebView = !!(window as any)?.chrome?.webview;

    if (!hasWebView) {
      console.error('❌ WebView2 não está disponível!');
      return { success: false, error: 'WebView2 not available' };
    }

    try {
      const testPayload = {
        action: 'test:ping',
        requestId: `test-${Date.now()}`,
        data: { timestamp: Date.now(), source: 'manual-test' }
      };

      (window as any).chrome.webview.postMessage(testPayload);
      console.log('✅ Mensagem de teste enviada:', testPayload);
      sendToTerminal('📤 [TEST] Mensagem de teste enviada', testPayload, LogCategory.CONNECTION);

      return { success: true, payload: testPayload };
    } catch (e) {
      console.error('❌ Erro ao enviar mensagem de teste:', e);
      return { success: false, error: e instanceof Error ? e.message : String(e) };
    }
  };

  // ✅ NOVO (2025): Expor corelCommunication globalmente para debugging
  (window as any).corelCommunication = corelCommunication;

  // ✅ NOVO: Função para forçar flush da fila pendente
  // Uso: window.forceFlushQueue() no console do navegador
  (window as any).forceFlushQueue = () => {
    console.log('🔄 Forçando flush da fila...');
    corelCommunication.forceFlushQueue();
    return corelCommunication.getDiagnostics();
  };

  console.log('🔧 [DEBUG] Funções de diagnóstico disponíveis:');
  console.log('   - window.diagnoseWebView() - Diagnóstico completo');
  console.log('   - window.testWebViewMessage() - Testar envio de mensagem');
  console.log('   - window.corelCommunication.getDiagnostics() - Status da comunicação');
  console.log('   - window.forceFlushQueue() - Forçar flush da fila pendente');
}

/**
 * Handlers globais de erro para capturar erros não tratados
 * Todos os erros são enviados ao sendToTerminal para centralização
 */
if (typeof window !== 'undefined') {
  // Handler para erros JavaScript não capturados
  window.addEventListener('error', (event) => {
    const errorMsg = event.message || '';
    const isLauncherError = errorMsg.includes('launcher') || errorMsg.includes('booting');
    
    sendToTerminal(isLauncherError ? '🚨 [GLOBAL_ERROR] Erro relacionado ao Launcher' : '❌ [GLOBAL_ERROR] Erro JavaScript não capturado', {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      error: event.error?.stack || event.error?.message || String(event.error),
      isLauncherError,
      timestamp: new Date().toISOString()
    });

    // Manter comportamento padrão (console)
    console.error('Global error:', event);
  });

  // Handler para promises rejeitadas não tratadas
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const reasonStr = String(reason);
    const isLauncherError = reasonStr.includes('launcher') || reasonStr.includes('booting') || reasonStr.includes('session_required');
    
    sendToTerminal(isLauncherError ? '🚨 [UNHANDLED_PROMISE] Promise rejeitada - possível erro de Launcher' : '❌ [UNHANDLED_PROMISE] Promise rejeitada não tratada', {
      reason: event.reason,
      error: reason?.stack || reason?.message || reasonStr,
      isLauncherError,
      timestamp: new Date().toISOString()
    });

    // Manter comportamento padrão (console)
    console.error('Unhandled promise rejection:', event.reason);
  });
}

/**
 * Inicializa comunicação WebView2 ↔ C# aguardando bootstrap.
 * 
 * Tenta capturar sessionToken e hmacSecret via:
 * 1. Mensagem de bootstrap do C# (ativo)
 * 2. Fallback para window.sessionToken (passivo)
 * 
 * ⚠️ IMPORTANTE: HMAC é OBRIGATÓRIO em TODOS os modos (dev e prod)
 * Se você está rodando npm run dev sem backend C#, as mensagens serão bloqueadas.
 * 
 * ✅ MELHORADO (2025): Adicionado diagnóstico detalhado para debug em produção HTTPS.
 */
async function initializeWebViewCommunication(): Promise<void> {
  try {
    const hasWebView = !!(window as any)?.chrome?.webview;
    const windowToken = (window as any)?.sessionToken;
    const windowHmac = (window as any)?.sessionCrypto?.hmacKeyB64;

    // ✅ NOVO: Diagnóstico completo do ambiente
    const envDiagnosis = {
      protocol: window.location.protocol,
      hostname: window.location.hostname,
      port: window.location.port || 'default',
      origin: window.location.origin,
      isHttps: window.location.protocol === 'https:',
      isProduction: window.location.hostname !== 'localhost' && !window.location.hostname.startsWith('192.168.'),
      userAgent: navigator.userAgent.includes('WebView2') ? 'WebView2' : 'Browser'
    };

    sendToTerminal('🚀 [INIT] Iniciando comunicação WebView2...', {
      dockerId: (window as any)?.dockerId || 'not-provided',
      hasWebView,
      windowTokenPresent: !!windowToken,
      windowTokenLength: windowToken?.length ?? 0,
      windowHmacPresent: !!windowHmac,
      // ✅ NOVO: Diagnóstico do ambiente
      environment: envDiagnosis
    }, LogCategory.CONNECTION);

    // ✅ NOVO: Log de alerta se estiver em HTTPS sem WebView2
    if (envDiagnosis.isHttps && !hasWebView) {
      sendToTerminal('⚠️ [INIT] HTTPS detectado mas WebView2 não disponível', {
        possibleCauses: [
          'Página aberta em navegador normal (não no Docker do CorelDRAW)',
          'WebView2 ainda não injetou variáveis',
          'Erro na inicialização do C#'
        ],
        solution: 'Abra a página a partir do Docker do CorelDRAW'
      }, LogCategory.CONNECTION);
    }

    // ✅ Timeout aumentado para 10s conforme MVP Fix de refresh (F5)
    await corelCommunication.initializeFromBootstrap(10000);
    sendToTerminal('✅ [INIT] WebView2 comunicação inicializada', {
      environment: envDiagnosis
    }, LogCategory.CONNECTION);

    // Verificar se variáveis críticas estão disponíveis
    const hasSessionToken = !!(window as any).sessionToken;
    const hasHmacSecret = !!(window as any).sessionCrypto?.hmacKeyB64;
    const dockerId = (window as any)?.dockerId;

    if (!hasSessionToken || !hasHmacSecret) {
      sendToTerminal('⚠️ [INIT] Variáveis de autenticação não disponíveis', {
        hasSessionToken,
        hasHmacSecret,
        dockerId: dockerId || 'not-provided',
        help: 'Abra o CorelDRAW com o Add-on instalado e abra o Docker/WebView a partir do CorelDRAW'
      }, LogCategory.ERROR);
    } else {
      sendToTerminal('✅ [INIT] Variáveis de autenticação disponíveis', {
        hasSessionToken: true,
        hasHmacSecret: true,
        dockerId: dockerId || 'not-provided',
        sessionTokenPreview: hasSessionToken ? `${(window as any).sessionToken.substring(0, 8)}...` : undefined
      }, LogCategory.CONNECTION);
    }

    // Iniciar health check periódico — somente se WebView2 disponível
    if (hasWebView) {
      startHealthCheck();
    }

  } catch (error) {
    // ✅ MELHORADO: Diagnóstico detalhado em caso de falha
    const failureDiagnosis = {
      protocol: window.location.protocol,
      hostname: window.location.hostname,
      isHttps: window.location.protocol === 'https:',
      hasWebView: !!(window as any)?.chrome?.webview,
      hasChrome: !!(window as any)?.chrome,
      hasSessionToken: !!(window as any)?.sessionToken,
      hasHmacKey: !!(window as any)?.sessionCrypto?.hmacKeyB64,
      dockerId: (window as any)?.dockerId || 'not-provided',
      userAgent: navigator.userAgent.substring(0, 100)
    };

    sendToTerminal('⚠️ [INIT] Não foi possível inicializar comunicação WebView2', {
      error: error instanceof Error ? error.message : String(error),
      note: 'A aplicação funcionará mas comandos para CorelDRAW falharão',
      diagnosis: failureDiagnosis,
      // ✅ NOVO: Sugestões de diagnóstico
      troubleshooting: [
        'Verifique se o Docker do CorelDRAW está aberto',
        'Verifique se o add-on C# está instalado e ativo',
        'Tente pressionar F5 para recarregar',
        'Verifique o console do C# para erros'
      ]
    }, LogCategory.ERROR);
  }
}

/**
 * Listener para evento de reinjeção de sessão após refresh (F5).
 * 
 * O C# dispara `oneprint:session-reinjected` quando detecta navegação/refresh
 * e reinjeta as variáveis de sessão (sessionToken, sessionCrypto, dockerId).
 * 
 * Este listener serve como fallback: se o bootstrap inicial falhou,
 * podemos tentar novamente quando o C# sinalizar que a sessão foi reinjetada.
 * 
 * ✅ MELHORADO: Agora atualiza tanto sessionToken quanto HMAC secret,
 * e tenta reinicializar se o bootstrap inicial falhou.
 */
if (typeof window !== 'undefined') {
  window.addEventListener('oneprint:session-reinjected', ((event: CustomEvent) => {
    const windowToken = (window as any)?.sessionToken;
    const windowHmac = (window as any)?.sessionCrypto?.hmacKeyB64;

    // Validar formato do token (GUID: 36 chars, ou Hex: 64 chars)
    const guidRegex = /^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$/;
    const isGuid = windowToken ? guidRegex.test(String(windowToken)) : false;
    const isHex64 = windowToken ? (String(windowToken).length === 64 && /^[a-fA-F0-9]+$/.test(String(windowToken))) : false;
    const tokenValid = isGuid || isHex64;

    sendToTerminal('🔄 [SESSION_REINJECTED] Sessão reinjetada pelo C# após navegação', {
      detail: event.detail,
      hasSessionToken: !!windowToken,
      sessionTokenLength: windowToken?.length ?? 0,
      tokenFormat: isGuid ? 'GUID (36 chars)' : (isHex64 ? 'Hex (64 chars)' : 'inválido'),
      tokenValid,
      hasHmacKey: !!windowHmac,
      hmacLength: windowHmac?.length ?? 0,
      dockerId: (window as any)?.dockerId || 'unknown',
      timestamp: Date.now(),
      wasBootstrapInitialized: corelCommunication.isBootstrapInitialized()
    }, LogCategory.CONNECTION);

    // Atualizar sessão completa (token + HMAC) usando método dedicado
    const success = corelCommunication.updateSessionFromReinjection();

    if (success) {
      // Se bootstrap inicial falhou, tentar reinicializar completamente
      if (!corelCommunication.isBootstrapInitialized()) {
        sendToTerminal('🔄 [SESSION_REINJECTED] Reinicializando comunicação após falha inicial', {}, LogCategory.CONNECTION);

        // Tentar reinicializar de forma assíncrona (não bloqueia)
        setTimeout(async () => {
          try {
            // Usar timeout menor já que variáveis já estão disponíveis
            await corelCommunication.initializeFromBootstrap(3000);
            sendToTerminal('✅ [SESSION_REINJECTED] Comunicação reinicializada com sucesso', {}, LogCategory.CONNECTION);
          } catch (error) {
            // Se falhar, pelo menos temos token e HMAC atualizados
            sendToTerminal('⚠️ [SESSION_REINJECTED] Reinicialização falhou, mas sessão foi atualizada', {
              error: error instanceof Error ? error.message : String(error)
            }, LogCategory.CONNECTION);
          }
        }, 100);
      } else {
        sendToTerminal('✅ [SESSION_REINJECTED] Sessão atualizada (bootstrap já estava inicializado)', {}, LogCategory.CONNECTION);
      }
    } else {
      // ✅ MELHORADO: Mostrar motivo específico da falha
      const reason = !windowToken
        ? 'sessionToken não disponível em window'
        : windowToken.length !== 64
          ? `sessionToken com tamanho inválido (${windowToken.length}, esperado: 64)`
          : !/^[a-fA-F0-9]+$/.test(String(windowToken))
            ? 'sessionToken não é hexadecimal válido'
            : 'erro desconhecido ao atualizar';

      sendToTerminal('⚠️ [SESSION_REINJECTED] Falha ao atualizar sessão', {
        reason,
        windowTokenPresent: !!windowToken,
        windowTokenLength: windowToken?.length ?? 0,
        windowTokenIsHex: windowToken ? /^[a-fA-F0-9]+$/.test(String(windowToken)) : false,
        windowHmacPresent: !!windowHmac,
        dockerId: (window as any)?.dockerId
      }, LogCategory.ERROR);
    }
  }) as EventListener);
}

__bench('Before initializeWebViewCommunication');

// Iniciar bootstrap em paralelo com renderização
// (não bloqueia a UI, mas tenta capturar token o mais cedo possível)
initializeWebViewCommunication();

__bench('After initializeWebViewCommunication (async, not awaited)');

// ✅ Configuração do QueryClient
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60000, // 1 minuto
      gcTime: 10 * 60 * 1000, // 10 minutos
      refetchOnWindowFocus: false, // Evita refetch ao trocar de aba no docker
      retry: 1,
    },
  },
});

__bench('Before createRoot().render()');

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AppRouter>
          <WebSocketProvider>
            <NotificationProvider>
              <ConfirmationProvider>
                <KeyboardProvider>
                  <ConnectivityProvider>
                    <PermissionProvider>
                      <ProdutoCategoriasProvider>
                        <CorelProvider>
                          <HealthStatusProvider>
                            <OrderProvider>
                              <App />
                            </OrderProvider>
                          </HealthStatusProvider>
                        </CorelProvider>
                      </ProdutoCategoriasProvider>
                    </PermissionProvider>
                  </ConnectivityProvider>
                </KeyboardProvider>
              </ConfirmationProvider>
            </NotificationProvider>
          </WebSocketProvider>
        </AppRouter>
      </AuthProvider>
    </QueryClientProvider>
  </StrictMode>
);

// Inicializa o bridge de upload (ouve mensagens do C# via WebView2)
try { initLayoutUploadBridge(); } catch { }
